/**
 * Crawlable URLs for the programme filters.
 *
 * Filtering used to be pure component state, so a visitor looking at
 * "School of ICT, Media and Engineering" or "MASTERS" was still sitting on
 * /programmes — nothing for Google to index and nothing anyone could share or
 * run ads to. Every filter chip is now a real link backed by a real route:
 *
 *   /programmes/school/school-of-ict-media-and-engineering
 *   /programmes/level/postgraduate
 *   /programmes/qualification/masters
 *
 * Each of those renders the same page pre-filtered, with its own title,
 * description and canonical, and each is listed in the sitemap.
 */
import { programmes } from "../Tuition Fees/programmes";

export const QUALIFICATIONS = [
  "ALL",
  "PHD",
  "MASTERS",
  "POSTGRADUATE DIPLOMA",
  "UNDERGRADUATE",
  "DIPLOMA",
  "CERTIFICATE",
  "TVET",
  "CPC",
];

/**
 * Market-facing labels. The catalog stores the academic term
 * "UNDERGRADUATE", but prospective students search for "degree", so every
 * public surface — tabs, headings, SEO copy, schema and URLs — says Degree.
 */
export const QUALIFICATION_LABELS = {
  ALL: "All",
  PHD: "PhD",
  MASTERS: "Masters",
  "POSTGRADUATE DIPLOMA": "Postgraduate Diploma",
  UNDERGRADUATE: "Degree",
  DIPLOMA: "Diploma",
  CERTIFICATE: "Certificate",
  TVET: "TVET",
  CPC: "CPC",
};

/** Same wording decision for study levels (Undergraduate level → Degree). */
export const LEVEL_LABELS = { Undergraduate: "Degree" };

export function qualificationLabel(value) {
  return QUALIFICATION_LABELS[String(value || "").toUpperCase()] || value;
}

export function levelLabel(value) {
  return LEVEL_LABELS[value] || value;
}

/** Normalise the card badge: any "undergraduate" variant reads "Degree". */
export function categoryBadge(value) {
  return /^undergraduate$/i.test(String(value || "").trim()) ? "Degree" : value;
}

/** URL slug per qualification — /programmes/qualification/degree. */
const QUALIFICATION_SLUGS = { UNDERGRADUATE: "degree" };

export function qualificationSlug(value) {
  const key = String(value || "").toUpperCase();
  return QUALIFICATION_SLUGS[key] || slugify(value);
}

export function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Unique, sorted school names taken from the programme catalog. */
export function allSchools() {
  return [...new Set(programmes.map((p) => p.school).filter(Boolean))].sort();
}

/** Unique, sorted study levels (Postgraduate, Undergraduate, TVET...). */
export function allLevels() {
  return [...new Set(programmes.map((p) => p.level).filter(Boolean))].sort();
}

/**
 * Qualifications that actually have programmes behind them. CPC short
 * courses live in their own catalog (src/ProfessionalCertifications/
 * cpcCourses.js) rather than programmes.js, but the tab, route, sitemap
 * entry and OG card are real, so CPC is always listed.
 */
export function allQualifications() {
  const present = new Set(
    programmes.map((p) => String(p.qualification || "").toUpperCase())
  );
  return QUALIFICATIONS.filter(
    (q) => q !== "ALL" && (q === "CPC" || present.has(q))
  );
}

/** Resolve a slug back to the original catalog value (case/spacing safe). */
export function findBySlug(values, slug) {
  if (!slug) return "";
  return values.find((value) => slugify(value) === String(slug).toLowerCase()) || "";
}

/** Build the URL for a given filter selection. */
export function filterPath({ school, level, qualification } = {}) {
  const parts = [];
  if (school) parts.push("school", slugify(school));
  if (level) parts.push("level", slugify(level));
  if (qualification && qualification !== "ALL") {
    parts.push("qualification", qualificationSlug(qualification));
  }
  return parts.length ? `/programmes/${parts.join("/")}` : "/programmes";
}

/**
 * Resolve a qualification slug back to the catalog value. Accepts both the
 * current slug (/degree) and the legacy one (/undergraduate) so old links and
 * bookmarks keep rendering the right page — the canonical always points at
 * the current slug.
 */
function findQualificationBySlug(slug) {
  if (!slug) return "";
  const target = String(slug).toLowerCase();
  return (
    allQualifications().find(
      (q) => qualificationSlug(q) === target || slugify(q) === target
    ) || ""
  );
}

/**
 * Turn the route params into the active filter values.
 * Unknown slugs simply fall back to "no filter" so a bad URL still renders
 * the full catalog instead of an empty page.
 */
export function activeFiltersFromParams(params = {}) {
  const routeParts = params.filterType
    ? [
        params.filterType, params.filterSlug,
        params.filterType2, params.filterSlug2,
        params.filterType3, params.filterSlug3,
      ]
    : [];
  const routeFilters = {};
  for (let i = 0; i < routeParts.length; i += 2) {
    if (routeParts[i] && routeParts[i + 1]) routeFilters[routeParts[i]] = routeParts[i + 1];
  }
  return {
    school: findBySlug(allSchools(), params.schoolSlug || routeFilters.school),
    level: findBySlug(allLevels(), params.levelSlug || routeFilters.level),
    qualification:
      findQualificationBySlug(params.qualificationSlug || routeFilters.qualification).toUpperCase() || "ALL",
  };
}

/** Every filter URL, for the sitemap generator and internal linking. */
export function allFilterPaths() {
  return [
    ...allSchools().map((s) => filterPath({ school: s })),
    ...allLevels().map((l) => filterPath({ level: l })),
    ...allQualifications().map((q) => filterPath({ qualification: q })),
  ];
}

/** Human labels + SEO copy for a filtered view. */
export function filterSeo({ school, level, qualification }, count) {
  const total = typeof count === "number" ? count : null;
  const suffix = total !== null ? `${total} programme${total === 1 ? "" : "s"}` : "programmes";
  const listed = total !== null ? `All ${suffix}` : "Programmes";

  if (school) {
    return {
      heading: `${school} Programmes`,
      title: `${school} Programmes in Kenya`,
      description: `${listed} offered by the ${school} at Zetech University, Kenya — entry requirements, duration, intakes and tuition fees compared side by side. Apply online today.`,
      keywords: `${school}, ${school} courses Kenya, Zetech University ${school}, courses in Kenya, university admission Kenya`,
      intro: `Compare every course offered by the ${school} at Zetech University, including entry requirements, study duration, intakes, campuses and tuition fees.`,
    };
  }
  if (level) {
    const label = levelLabel(level);
    return {
      heading: `${label} Programmes`,
      title: `${label} Courses in Kenya — Fees & Entry Requirements`,
      description: `${listed} at ${label} level at Zetech University, Kenya. Compare duration, January/May/September intakes, entry requirements and tuition fees. Apply online today.`,
      keywords: `${label} courses Kenya, ${label} programmes Kenya, ${label} fees Kenya, Zetech University ${label}, study in Kenya`,
      intro: `Browse every ${label.toLowerCase()} programme at Zetech University with the entry requirements, intakes, duration and tuition fees for each course.`,
    };
  }
  if (qualification && qualification !== "ALL") {
    // CPC short courses get their own marketing copy — the generic
    // "<Label> programmes in Kenya" template would read awkwardly.
    if (qualification === "CPC") {
      const courses =
        total !== null
          ? `All ${total} professional short courses`
          : "Professional short courses";
      return {
        heading: "Professional Short Courses (CPC)",
        title: "Professional Short Courses in Kenya — CPC Fees & Modules",
        description: `${courses} at Zetech University's Center for Professional Certifications — ICDL, Cisco, Huawei, programming, graphic design, accounting packages, languages and hospitality, from 2 weeks to 6 months. Fees from KES 5,000. Apply online today.`,
        keywords:
          "professional short courses Kenya, CPC courses Zetech University, ICDL Kenya, Cisco CCNA Kenya, Huawei certification Kenya, computer packages course Kenya, graphic design course Kenya, accounting packages Kenya, short courses Nairobi",
        intro:
          "Every professional certification course at the Center for Professional Certifications in one place — modules, study duration and fees — so you can pick a skill and apply online.",
      };
    }
    const label = qualificationLabel(qualification);
    const plural =
      label === "PhD"
        ? "PhD programmes"
        : label === "TVET"
        ? "TVET courses"
        : `${label} programmes`;
    return {
      heading: `${plural.charAt(0).toUpperCase()}${plural.slice(1)}`,
      title: `${plural.charAt(0).toUpperCase()}${plural.slice(1)} in Kenya — Fees & Requirements`,
      description: `${listed} at ${label} level at Zetech University, Kenya. Compare tuition fees, intakes, duration and entry requirements for every ${label} course, then apply online.`,
      keywords: `${plural} in Kenya, ${label} courses Kenya, ${label} fees Kenya, Zetech University ${label}, ${label} entry requirements Kenya`,
      intro: `Every ${label} course at Zetech University in one place — tuition fees, intakes, study duration, campuses and entry requirements, so you can compare before you apply.`,
    };
  }
  return {
    heading: "Our Academic Programmes",
    title: "Academic Programmes in Kenya — Courses, Fees & Intakes",
    description:
      "Explore Zetech University's PhD, masters, degree, diploma, certificate and TVET programmes in ICT, business, health sciences, law, education and engineering — with fees, intakes and entry requirements.",
    keywords:
      "courses in Kenya, university programmes Kenya, Zetech University courses, diploma courses Kenya, degree courses Kenya, TVET courses Kenya, certificate courses Kenya",
    intro:
      "Choose from PhD, masters, degree, diploma, certificate, TVET and professional programmes designed to equip you with industry-relevant knowledge, practical skills and a global mindset.",
  };
}

/**
 * Per-route social share artwork.
 *
 * scripts/generate-og-images.mjs renders one 1200x630 card per filter URL at
 * build time (heading, programme count, sample courses) into
 * public/images/og/programmes/, so a shared /programmes/qualification/phd link
 * previews PhD artwork instead of the generic sitewide card.
 */
export function filterOgImageKey(filters = {}) {
  const p = filterPath(filters);
  return p === "/programmes" ? "all" : p.replace("/programmes/", "").replace(/\//g, "-");
}

export function filterOgImage(filters = {}) {
  return `/images/og/programmes/${filterOgImageKey(filters)}.jpg`;
}
